// Pharmacy hand-off queue — Week 4, extended for Phase 3.b partial
// dispensing.
//
// Clinic-wide list of active medication_requests. The pharmacist (or the
// clinician when there's no dedicated pharmacist) marks each script as
// fully dispensed, partially dispensed, or cancelled. Role gating lives on
// the server (pharmacy.dispense / pharmacy.cancel). Rows are grouped by
// patient so a single visit's scripts render together (patients typically
// leave with several meds at once).
//
// Dispense actions (Phase 3.b):
//   Dispense & complete → POST /dispenses (is_partial=false) — the final
//                         (or sole) dispense; the script leaves the queue.
//   Dispense partial    → POST /dispenses (is_partial=true, quantity>0) —
//                         script stays on the queue; a "Dispense remainder"
//                         affordance pre-fills the quantity when the
//                         prescribed `dispense_qty` begins with a number.
//   Cancel              → POST /prescriptions/:id/cancel — retract without
//                         dispensing (hard to reverse in v1 so there's a
//                         confirm gate).
//
// Quantity + note live on each medication_dispenses row (migration 0080 for
// the table, 0102 for the is_partial flag). The queue hides a script once
// at least one non-superseded final dispense exists; partial dispenses
// accumulate in the row's `dispenses` array for the UI running total.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ApiError, pharmacyApi, type PharmacyQueueEntry } from '../lib/api';

export function PharmacyQueueScreen() {
    const [search, setSearch] = useState('');
    const { data, isLoading, isError, refetch, isFetching } = useQuery({
        queryKey: ['pharmacy-queue'],
        queryFn: () => pharmacyApi.queue(),
    });

    // Group prescriptions by patient (falling back to the encounter id when
    // patient is null, which shouldn't happen but keeps the render honest).
    const groups = useMemo(() => {
        if (!data) return [];
        const byKey = new Map<string, {
            key: string;
            patientName: string;
            patientPhone: string;
            oldestPrescribedAt: string;
            items: PharmacyQueueEntry[];
        }>();
        for (const rx of data.prescriptions) {
            const key = rx.patient?.id ?? `enc:${rx.encounter?.id ?? rx.id}`;
            const existing = byKey.get(key);
            if (existing) {
                existing.items.push(rx);
                if (rx.prescribed_at && rx.prescribed_at < existing.oldestPrescribedAt) {
                    existing.oldestPrescribedAt = rx.prescribed_at;
                }
            } else {
                byKey.set(key, {
                    key,
                    patientName: rx.patient?.full_name?.trim() || rx.patient?.phone_e164 || 'Unknown patient',
                    patientPhone: rx.patient?.phone_e164 ?? '',
                    oldestPrescribedAt: rx.prescribed_at ?? new Date().toISOString(),
                    items: [rx],
                });
            }
        }
        // Oldest patient-visit first so the physical queue is FIFO.
        return Array.from(byKey.values()).sort((a, b) =>
            a.oldestPrescribedAt.localeCompare(b.oldestPrescribedAt),
        );
    }, [data]);

    // Client-side patient filter — pharmacist is usually looking for a
    // specific person waiting at the counter, so we match against name
    // and phone. Drug name is intentionally excluded: browsing by drug
    // is a different task and would confusingly hide other items for
    // the same patient.
    const visibleGroups = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return groups;
        return groups.filter((g) =>
            g.patientName.toLowerCase().includes(q)
            || g.patientPhone.toLowerCase().includes(q),
        );
    }, [groups, search]);

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-slate-900 text-2xl font-semibold">Pharmacy queue</h1>
                    <p className="text-slate-500 text-sm mt-1">
                        Active prescriptions awaiting hand-off, oldest first.
                    </p>
                </div>
                <button
                    type="button"
                    className="pm-btn-outline whitespace-nowrap"
                    onClick={() => void refetch()}
                    disabled={isFetching}
                >
                    {isFetching ? 'Refreshing…' : 'Refresh'}
                </button>
            </div>

            {/* Quick search — kept above loading state so the layout
                doesn't shift once the data arrives. */}
            <div className="pm-card p-3">
                <label htmlFor="pharmacy-search" className="sr-only">
                    Search pharmacy queue
                </label>
                <input
                    id="pharmacy-search"
                    type="search"
                    className="pm-input"
                    placeholder="Search by patient name or phone"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            {isLoading && (
                <div className="pm-card p-8 text-center text-slate-500 text-sm">
                    Loading…
                </div>
            )}

            {isError && (
                <div className="pm-card p-6 text-center">
                    <div className="text-slate-800 font-medium">Can’t load the pharmacy queue</div>
                    <div className="text-slate-500 text-sm mt-1">
                        Check your connection and try again.
                    </div>
                </div>
            )}

            {data && groups.length === 0 && (
                <div className="pm-card p-8 text-center">
                    <div className="text-slate-800 font-medium">Nothing to dispense</div>
                    <div className="text-slate-500 text-sm mt-1">
                        New prescriptions land here as clinicians finalize encounters.
                    </div>
                </div>
            )}

            {groups.length > 0 && visibleGroups.length === 0 && (
                <div className="pm-card p-6 text-center">
                    <div className="text-slate-800 font-medium">No matches</div>
                    <div className="text-slate-500 text-sm mt-1">
                        Try a different name or phone number.
                    </div>
                </div>
            )}

            {visibleGroups.length > 0 && (
                <div className="space-y-4">
                    {visibleGroups.map((g) => (
                        <PatientGroup key={g.key} group={g} />
                    ))}
                </div>
            )}
        </div>
    );
}

interface Group {
    key: string;
    patientName: string;
    patientPhone: string;
    oldestPrescribedAt: string;
    items: PharmacyQueueEntry[];
}

function PatientGroup({ group }: { group: Group }) {
    const encounterId = group.items[0]?.encounter?.id ?? null;
    const waited = formatWaited(group.oldestPrescribedAt);
    return (
        <div className="pm-card overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-slate-900 font-medium">{group.patientName}</div>
                    <div className="text-slate-500 text-xs mt-0.5 flex items-center gap-2 flex-wrap">
                        {group.patientPhone && <span>{group.patientPhone}</span>}
                        {group.patientPhone && <span aria-hidden>·</span>}
                        <span>{group.items.length} item{group.items.length === 1 ? '' : 's'}</span>
                        <span aria-hidden>·</span>
                        <span>waiting {waited}</span>
                    </div>
                </div>
                {encounterId && (
                    <Link
                        to={`/encounters/${encounterId}`}
                        className="text-brand-700 hover:underline text-xs shrink-0"
                    >
                        View encounter →
                    </Link>
                )}
            </div>
            <ul className="divide-y divide-slate-100">
                {group.items.map((rx) => (
                    <li key={rx.id}>
                        <RxRow rx={rx} />
                    </li>
                ))}
            </ul>
        </div>
    );
}

function RxRow({ rx }: { rx: PharmacyQueueEntry }) {
    const qc = useQueryClient();
    const [note, setNote] = useState('');
    const [showNote, setShowNote] = useState(false);
    const [qtyInput, setQtyInput] = useState<string>('');
    const [error, setError] = useState<string | null>(null);

    // Partial-dispense accounting. The queue filter guarantees that any
    // dispenses attached here are either superseded or `is_partial=true` —
    // a non-superseded final dispense would have removed the row entirely.
    const prevPartials = useMemo(
        () => (rx.dispenses ?? []).filter((d) => d.superseded_by_id === null && d.is_partial),
        [rx.dispenses],
    );
    const dispensedSoFar = useMemo(
        () => prevPartials.reduce((sum, d) => sum + (Number(d.quantity) || 0), 0),
        [prevPartials],
    );
    const prescribedTotal = useMemo(() => parseLeadingNumber(rx.dispense_qty), [rx.dispense_qty]);
    const remainder = prescribedTotal != null
        ? Math.max(0, prescribedTotal - dispensedSoFar)
        : null;

    const parsedQty = Number(qtyInput);
    const qtyIsValid = qtyInput.trim() !== '' && Number.isFinite(parsedQty) && parsedQty > 0;

    const dispense = useMutation({
        mutationFn: (opts: { partial: boolean }) =>
            pharmacyApi.dispense(rx.id, {
                ...(qtyIsValid ? { quantity: parsedQty } : {}),
                ...(note.trim() ? { note: note.trim() } : {}),
                ...(opts.partial ? { is_partial: true } : {}),
            }),
        onSuccess: () => {
            setNote('');
            setShowNote(false);
            setQtyInput('');
            setError(null);
            void qc.invalidateQueries({ queryKey: ['pharmacy-queue'] });
            if (rx.encounter?.id) {
                void qc.invalidateQueries({ queryKey: ['encounter', rx.encounter.id] });
            }
        },
        onError: (e) => setError(prettyError(e)),
    });

    const cancel = useMutation({
        mutationFn: () => pharmacyApi.cancel(rx.id),
        onSuccess: () => {
            setError(null);
            void qc.invalidateQueries({ queryKey: ['pharmacy-queue'] });
            if (rx.encounter?.id) {
                void qc.invalidateQueries({ queryKey: ['encounter', rx.encounter.id] });
            }
        },
        onError: (e) => setError(prettyError(e)),
    });

    const busy = dispense.isPending || cancel.isPending;
    const partialDisabled = busy || !qtyIsValid;

    return (
        <div className="px-4 py-3">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-slate-900 font-medium">{rx.medication_name}</div>
                    <div className="text-slate-600 text-sm mt-0.5">
                        {[rx.dose, rx.route, rx.frequency, rx.duration].filter(Boolean).join(' · ')}
                    </div>
                    {rx.instructions && (
                        <div className="text-slate-500 text-xs mt-1 whitespace-pre-line">
                            {rx.instructions}
                        </div>
                    )}
                    {(rx.dispense_qty || (rx.refills ?? 0) > 0) && (
                        <div className="text-slate-500 text-xs mt-1 flex items-center gap-2 flex-wrap">
                            {rx.dispense_qty && <span>Dispense: {rx.dispense_qty}</span>}
                            {rx.dispense_qty && (rx.refills ?? 0) > 0 && <span aria-hidden>·</span>}
                            {(rx.refills ?? 0) > 0 && <span>Refills: {rx.refills}</span>}
                        </div>
                    )}
                    {prevPartials.length > 0 && (
                        <div className="mt-2 inline-flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 px-2 py-1 text-amber-800 text-xs">
                            <span className="font-medium">Partial</span>
                            <span>
                                {dispensedSoFar} dispensed
                                {prescribedTotal != null ? ` of ${prescribedTotal}` : ''}
                                {remainder != null ? ` · remainder ${remainder}` : ''}
                            </span>
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <button
                        type="button"
                        className="pm-btn-ghost text-xs"
                        onClick={() => setShowNote((s) => !s)}
                        disabled={busy}
                    >
                        {showNote ? 'Hide note' : 'Add note'}
                    </button>
                    <button
                        type="button"
                        className="pm-btn-outline text-xs"
                        onClick={() => {
                            if (window.confirm(`Cancel ${rx.medication_name}? This cannot be undone in V0.`)) {
                                cancel.mutate();
                            }
                        }}
                        disabled={busy}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="pm-btn-primary text-xs"
                        onClick={() => dispense.mutate({ partial: false })}
                        disabled={busy}
                    >
                        {dispense.isPending ? 'Dispensing…' : 'Dispense & complete'}
                    </button>
                </div>
            </div>
            {/* Quantity + partial controls. Compact bar — one row until the
                pharmacist opts into a note. */}
            <div className="mt-2 flex flex-wrap items-center gap-2">
                <label className="text-xs text-slate-500" htmlFor={`qty-${rx.id}`}>Qty</label>
                <input
                    id={`qty-${rx.id}`}
                    type="number"
                    min={1}
                    step={1}
                    inputMode="numeric"
                    className="pm-input w-24 text-sm"
                    placeholder={remainder != null ? String(remainder) : ''}
                    value={qtyInput}
                    onChange={(e) => setQtyInput(e.target.value)}
                    disabled={busy}
                />
                {remainder != null && remainder > 0 && (
                    <button
                        type="button"
                        className="pm-btn-ghost text-xs"
                        onClick={() => setQtyInput(String(remainder))}
                        disabled={busy}
                    >
                        Dispense remainder ({remainder})
                    </button>
                )}
                <button
                    type="button"
                    className="pm-btn-outline text-xs"
                    onClick={() => dispense.mutate({ partial: true })}
                    disabled={partialDisabled}
                    title={qtyIsValid ? '' : 'Enter a quantity to record a partial dispense.'}
                >
                    Dispense partial
                </button>
            </div>
            {showNote && (
                <div className="mt-2">
                    <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Optional note (substitution, out-of-stock, etc.)"
                        className="pm-input w-full text-sm"
                        rows={2}
                        disabled={busy}
                    />
                </div>
            )}
            {error && (
                <div className="mt-2 text-rose-600 text-xs">{error}</div>
            )}
        </div>
    );
}

// Pulls a leading integer out of a free-text `dispense_qty` ("30 tablets",
// "20 ml", "1 pack"). Returns null when the value doesn't start with a
// number — the UI falls back to "no remainder available" in that case.
function parseLeadingNumber(raw: string | null | undefined): number | null {
    if (!raw) return null;
    const m = raw.trim().match(/^(\d+(?:\.\d+)?)/);
    if (!m) return null;
    const n = Number(m[1]);
    return Number.isFinite(n) && n > 0 ? n : null;
}

// Same coarse relative-time formatting as WorkListScreen. Kept local — one
// screen doesn't earn a shared helper yet.
function formatWaited(prescribedAt: string): string {
    const t = new Date(prescribedAt).getTime();
    if (!Number.isFinite(t)) return 'recently';
    const diffMin = Math.max(0, Math.round((Date.now() - t) / 60_000));
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h`;
    const diffDay = Math.round(diffHr / 24);
    return `${diffDay}d`;
}

function prettyError(e: unknown): string {
    if (!(e instanceof ApiError)) return 'Something went wrong.';
    switch (e.code) {
        case 'invalid_transition':
            return 'This prescription is no longer active.';
        case 'forbidden_other_clinic':
            return 'This prescription belongs to another clinic.';
        case 'not_found':
            return 'This prescription no longer exists.';
        case 'insufficient_role':
            return 'Your role can’t dispense or cancel prescriptions.';
        default:
            return 'Update failed. Try again.';
    }
}
